/**
 * EditSidebar — responsive shell for the Edition panel.
 *
 * Wraps the unified <EditionMenu> in the sliding / mobile-aware container.
 * All the field rendering lives in EditionMenu, which is also reused in
 * Simple mode to guarantee a single source of truth for the edit form.
 */

import { ChevronLeft, ChevronRight, FileEdit } from 'lucide-react';
import type { CVData } from '../../types';
import type { getTType } from '../../contexts/I18nContext';
import type { TemplateMetaItem, CommonInfo, FieldVisibility } from '../../hooks/useCvState';
import type { UseCvEditingReturn } from '../../hooks/useCvEditing';
import { EditionMenu } from './EditionMenu';

export interface EditSidebarProps {
  t: getTType;

  // Layout / responsive
  isMobile: boolean;
  mobileTab: 'edition' | 'ats' | 'preview';
  isSidebarOpen: boolean;
  setIsSidebarOpen: (updater: (prev: boolean) => boolean) => void;

  // CV state
  data: CVData;
  commonInfo: CommonInfo;
  fieldVisibility: FieldVisibility;
  setData: React.Dispatch<React.SetStateAction<CVData>>;
  editing: UseCvEditingReturn;

  // Template versioning
  templateMeta: TemplateMetaItem[];
  activeTemplate: string;
  setActiveTemplate: (id: string) => void;

  // Optional version management actions
  onRenameTemplate?: (id: string, newName: string) => void;
  onDuplicateTemplate?: (id: string) => void;
  onDeleteTemplate?: (id: string) => void;
}

export function EditSidebar({
  t,
  isMobile,
  mobileTab,
  isSidebarOpen,
  setIsSidebarOpen,
  data,
  commonInfo,
  fieldVisibility,
  setData,
  editing,
  templateMeta,
  activeTemplate,
  setActiveTemplate,
  onRenameTemplate,
  onDuplicateTemplate,
  onDeleteTemplate,
}: EditSidebarProps) {
  return (
    <div
      className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
        isMobile
          ? `absolute left-0 right-0 bottom-0 mobile-panel-top ${
              mobileTab === 'edition'
                ? 'z-10 flex flex-col'
                : 'z-0 opacity-0 pointer-events-none invisible'
            }`
          : isSidebarOpen
            ? 'w-0 md:w-[340px]'
            : 'w-0 md:w-0'
      }`}
    >
      <button
        onClick={() => setIsSidebarOpen((prev) => !prev)}
        className="absolute top-[35%] left-full -translate-y-1/2 -translate-x-1/2 z-30 w-8 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white/70 hover:text-white hidden md:flex items-center justify-center shadow-lg shadow-black/30"
        title={isSidebarOpen ? t('nav.hideEdition') : t('nav.showEdition')}
        aria-label={isSidebarOpen ? t('nav.hideEdition') : t('nav.showEdition')}
      >
        {isSidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>
      <div
        className={`${isMobile ? 'flex-1 min-h-0' : 'h-full min-h-0'} border-r border-white/10 glass ${isMobile ? '' : 'overflow-hidden'} no-print transition-[opacity,transform] duration-300 ease-in-out ${isMobile ? 'mobile-scroll-panel' : ''} ${
          isSidebarOpen || (isMobile && mobileTab === 'edition')
            ? 'opacity-100 translate-x-0'
            : 'opacity-0 -translate-x-2 pointer-events-none'
        } ${isSidebarOpen && !isMobile ? 'fixed inset-y-0 left-0 z-40 w-full max-w-[100vw] md:relative md:inset-auto md:z-0 md:max-w-none' : ''} ${isMobile ? 'relative' : ''} flex flex-col`}
      >
        {/* Header (desktop only) */}
        {!isMobile && (
          <div className="px-4 pt-4 pb-2 shrink-0">
            <h1
              className="flex items-center justify-between gap-2 text-xl font-black tracking-tight text-white cursor-pointer hover:text-blue-300 transition-colors"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
            >
              <span className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
                  <FileEdit size={18} />
                </div>
                Edition
              </span>
              {isSidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </h1>
          </div>
        )}

        {/* Unified Edition menu — on mobile the parent (.mobile-scroll-panel) scrolls */}
        <div className={`flex-1 min-h-0 ${isMobile ? '' : 'overflow-y-auto'} scrollbar-hide`}>
          <EditionMenu
            key={isSidebarOpen ? 'open' : 'closed'}
            t={t}
            data={data}
            commonInfo={commonInfo}
            fieldVisibility={fieldVisibility}
            setData={setData}
            editing={editing}
            templateMeta={templateMeta}
            activeTemplate={activeTemplate}
            setActiveTemplate={setActiveTemplate}
            onRenameTemplate={onRenameTemplate}
            onDuplicateTemplate={onDuplicateTemplate}
            onDeleteTemplate={onDeleteTemplate}
          />
        </div>
      </div>
    </div>
  );
}
